// folders-store.svelte.ts — chat folders (SAY-1820) for the sidebar.
// Module-level reactive state shared by FolderList, SessionList (which hides
// chats that live in a folder), the bulk action bar and the home page's
// "new chat in folder" flow. The manager owns the data (/folders); every
// mutation here is optimistic and reconciled from the server's answer.

import {
	createFolder as apiCreate,
	deleteFolder as apiDelete,
	listFolders as apiList,
	moveSessionsToFolder as apiMove,
	setSessionPinned as apiPin,
	updateFolder as apiUpdate,
	type ChatFolder,
	type ChatFoldersView,
} from './api';

export type { ChatFolder };

const folders = $state<ChatFolder[]>([]);
const membership = $state<Record<string, string>>({});
const pinned = $state<string[]>([]);
let loadState = $state<'idle' | 'loading' | 'ready' | 'error'>('idle');

export function getFolders(): ChatFolder[] {
	return folders;
}

export function getFolderMembership(): Record<string, string> {
	return membership;
}

export function foldersLoadState(): 'idle' | 'loading' | 'ready' | 'error' {
	return loadState;
}

export function folderOf(sessionId: string): string | null {
	return membership[sessionId] ?? null;
}

export function folderById(id: string | null | undefined): ChatFolder | undefined {
	return id ? folders.find((f) => f.id === id) : undefined;
}

export function isPinned(sessionId: string): boolean {
	return pinned.includes(sessionId);
}

/** Pin order (later = pinned more recently); -1 when not pinned. */
export function pinRank(sessionId: string): number {
	return pinned.indexOf(sessionId);
}

function apply(view: ChatFoldersView): void {
	folders.length = 0;
	folders.push(...view.folders);
	for (const k of Object.keys(membership)) delete membership[k];
	Object.assign(membership, view.membership);
	pinned.length = 0;
	pinned.push(...(view.pinned ?? []));
}

/** Record a chat's folder locally right after the manager filed it (POST
 *  /sessions with folderId), so the sidebar shows it in the folder at once. */
export function assignLocally(sessionId: string, folderId: string): void {
	membership[sessionId] = folderId;
}

/** Optimistic pin toggle; the server's view wins once it answers. */
export async function setPinned(sessionId: string, on: boolean): Promise<void> {
	const was = pinned.includes(sessionId);
	if (on && !was) pinned.push(sessionId);
	if (!on && was) pinned.splice(pinned.indexOf(sessionId), 1);
	try {
		apply(await apiPin(sessionId, on));
	} catch (err) {
		if (was && !pinned.includes(sessionId)) pinned.push(sessionId);
		if (!was && pinned.includes(sessionId)) pinned.splice(pinned.indexOf(sessionId), 1);
		throw err;
	}
}

export async function refreshFolders(): Promise<void> {
	loadState = loadState === 'ready' ? 'ready' : 'loading';
	try {
		apply(await apiList());
		loadState = 'ready';
	} catch (err) {
		// A manager that predates /folders (404) simply has no folders — the
		// sidebar then shows the flat list; nothing to shout about.
		loadState = 'error';
		console.warn('[folders] load failed', err);
	}
}

export async function createFolder(input: { name: string; instructions?: string }): Promise<ChatFolder> {
	const created = await apiCreate(input);
	folders.push(created);
	return created;
}

export async function updateFolder(id: string, patch: { name?: string; instructions?: string }): Promise<ChatFolder> {
	const updated = await apiUpdate(id, patch);
	const i = folders.findIndex((f) => f.id === id);
	if (i >= 0) folders[i] = updated;
	return updated;
}

export async function deleteFolder(id: string): Promise<void> {
	await apiDelete(id);
	const i = folders.findIndex((f) => f.id === id);
	if (i >= 0) folders.splice(i, 1);
	for (const [sid, fid] of Object.entries(membership)) if (fid === id) delete membership[sid];
}

/** Optimistic move; the server's view wins once it answers. */
export async function moveToFolder(sessionIds: string[], folderId: string | null): Promise<void> {
	const before = sessionIds.map((sid) => [sid, membership[sid]] as const);
	for (const sid of sessionIds) {
		if (folderId === null) delete membership[sid];
		else membership[sid] = folderId;
	}
	try {
		apply(await apiMove(sessionIds, folderId));
	} catch (err) {
		for (const [sid, prev] of before) {
			if (prev === undefined) delete membership[sid];
			else membership[sid] = prev;
		}
		throw err;
	}
}

/** Forget a chat's membership locally (after a permanent delete). */
export function forgetSession(sessionId: string): void {
	delete membership[sessionId];
	const i = pinned.indexOf(sessionId);
	if (i >= 0) pinned.splice(i, 1);
}
